/**
 * Shadow-compare helper for venue resolution rollout.
 * Logs mismatch between legacy venue text and SoT venue text.
 * Only logs when there's a real difference (after normalization).
 *
 * Uses PostHog for tracking + console.warn in dev.
 */
import posthog from 'posthog-js';
import type { VenueKind } from './resolveVenueText';

type Surface =
  | 'landing'
  | 'explore_list'
  | 'explore_map'
  | 'event_detail'
  | 'producer_bio'
  | 'producer_events'
  | 'bookings'
  | 'place_context';

function normalize(text: string | null | undefined): string {
  return (text ?? '').trim().toLowerCase();
}

/**
 * Compare legacy venue text with SoT venue text.
 * Only logs when there's a mismatch.
 */
export function compareVenue(
  surface: Surface,
  eventId: string,
  legacyText: string | null | undefined,
  sotText: string,
  venueKind: VenueKind,
): void {
  const legacyNorm = normalize(legacyText);
  const sotNorm = normalize(sotText);

  if (legacyNorm === sotNorm) return;

  // Mismatch detected
  const payload = {
    event_id: eventId,
    surface,
    legacy_venue: legacyText ?? null,
    sot_venue: sotText,
    venue_kind: venueKind,
  };

  if (import.meta.env.DEV) {
    console.warn('[venue-shadow] Mismatch detected:', payload);
  }

  try {
    posthog.capture('event_venue_mismatch_detected', payload);
  } catch {
    // fail-safe: never break UI for telemetry
  }
}
